import ApiClient from './apiClient';

export interface Country {
  id: number;
  name: string;
  iso3: string;
  iso2: string;
  numeric_code: string;
  phone_code: string;
  capital: string;
  currency: string;
  currency_name: string;
  currency_symbol: string;
  tld: string;
  native_name: string;
  region: string;
  subregion: string;
  nationality: string;
  timezones: any[];
  translations: any;
  latitude: string;
  longitude: string;
  emoji: string;
  emoji_u: string;
  states?: State[];
}

export interface State {
  id: number;
  name: string;
  state_code: string;
  latitude: string;
  longitude: string;
  type: string;
  cities?: City[];
}

export interface City {
  id: number;
  name: string;
  latitude: string;
  longitude: string;
}

export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
  links?: any;
  meta?: any;
}

export interface PaginatedResponse<T> {
  data: T[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: any[];
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

// Get all countries with optional filtering
const getCountries = async (params?: {
  page?: number;
  per_page?: number;
  name?: string;
  code?: string;
  with_states?: boolean;
  with_cities?: boolean;
  search?: string;
  select_country?: number;
}): Promise<any> => {

  try {
    // Create a clean params object to prevent undefined values
    const searchParams: Record<string, any> = {};
    
    if (params) {
      // Only add defined parameters
      if (params.page !== undefined) searchParams.page = params.page;
      if (params.per_page !== undefined) searchParams.per_page = params.per_page;
      if (params.code !== undefined) searchParams.code = params.code;
      if (params.with_states !== undefined) searchParams.with_states = params.with_states;
      if (params.with_cities !== undefined) searchParams.with_cities = params.with_cities;
      if (params.select_country !== undefined) searchParams.select_country = params.select_country;
      
      // Search takes precedence over name for better UX
      if (params.search !== undefined && params.search !== '') {
        searchParams.name = params.search;
      } else if (params.name !== undefined && params.name !== '') {
        searchParams.name = params.name;
      }
    }
    
    // Make the API request
    const response = await ApiClient.get<any>('/countries', searchParams);

    return response;
  } catch (error) {
    console.error('Error in getCountries:', error);
    throw error;
  }
};

// Get a specific country by ID
const getCountry = async (id: number, params?: {
  with_states?: boolean;
  with_cities?: boolean;
}): Promise<ApiResponse<Country>> => {
  return ApiClient.get<ApiResponse<Country>>(`/countries/${id}`, params);
};

// Get all states with optional filtering
const getStates = async (params?: {
  page?: number;
  per_page?: number;
  country_id?: number;
  name?: string;
  code?: string;
  with_cities?: boolean;
  search?: string;
  select_state?: number;
}): Promise<ApiResponse<PaginatedResponse<State>>> => {
  try {
    // Create a clean params object to prevent undefined values
    const searchParams: Record<string, any> = {};
    
    if (params) {
      // Only add defined parameters
      if (params.page !== undefined) searchParams.page = params.page;
      if (params.per_page !== undefined) searchParams.per_page = params.per_page;
      if (params.country_id !== undefined) searchParams.country_id = params.country_id;
      if (params.code !== undefined) searchParams.code = params.code;
      if (params.with_cities !== undefined) searchParams.with_cities = params.with_cities;
      if (params.select_state !== undefined) searchParams.select_state = params.select_state;
      
      // Search takes precedence over name for better UX
      if (params.search !== undefined && params.search !== '') {
        searchParams.name = params.search;
      } else if (params.name !== undefined && params.name !== '') {
        searchParams.name = params.name;
      }
    }
    
    // Make the API request
    const response = await ApiClient.get<ApiResponse<PaginatedResponse<State>>>('/states', searchParams);
    return response;
  } catch (error) {
    console.error('Error in getStates:', error);
    throw error;
  }
};

// Get a specific state by ID
const getState = async (id: number, params?: {
  with_cities?: boolean;
}): Promise<ApiResponse<State>> => {
  return ApiClient.get<ApiResponse<State>>(`/states/${id}`, params);
};

// Get all cities with optional filtering
const getCities = async (params?: {
  page?: number;
  per_page?: number;
  state_id?: number;
  name?: string;
  search?: string;
  select_city?: number;
}): Promise<ApiResponse<PaginatedResponse<City>>> => {
  try {
    // Create a clean params object to prevent undefined values
    const searchParams: Record<string, any> = {};
    
    if (params) {
      // Only add defined parameters
      if (params.page !== undefined) searchParams.page = params.page;
      if (params.per_page !== undefined) searchParams.per_page = params.per_page;
      if (params.state_id !== undefined) searchParams.state_id = params.state_id;
      if (params.select_city !== undefined) searchParams.select_city = params.select_city;
      
      // Search takes precedence over name for better UX
      if (params.search !== undefined && params.search !== '') {
        searchParams.name = params.search;
      } else if (params.name !== undefined && params.name !== '') {
        searchParams.name = params.name;
      }
    }
    
    // Make the API request
    const response = await ApiClient.get<ApiResponse<PaginatedResponse<City>>>('/cities', searchParams);
    return response;
  } catch (error) {
    console.error('Error in getCities:', error);
    throw error;
  }
};

// Get a specific city by ID
const getCity = async (id: number): Promise<ApiResponse<City>> => {
  return ApiClient.get<ApiResponse<City>>(`/cities/${id}`);
};

// Get a specific country by ID - Direct function export
export const getCountryById = async (id: number | string): Promise<Country | null> => {
  try {
    const response = await getCountry(Number(id));
    return response?.data || null;
  } catch (error) {
    console.error('Error fetching country by ID:', error);
    return null;
  }
};

// Get a specific state by ID - Direct function export
export const getStateById = async (countryId: number | string, id: number | string): Promise<State | null> => {
  try {
    const response = await getState(Number(id));
    return response?.data || null;
  } catch (error) {
    console.error('Error fetching state by ID:', error);
    return null;
  }
};

// Get a specific city by ID - Direct function export
export const getCityById = async (stateId: number | string, id: number | string): Promise<City | null> => {
  try {
    const response = await getCity(Number(id));
    return response?.data || null;
  } catch (error) {
    console.error('Error fetching city by ID:', error);
    return null;
  }
};

const locationService = {
  getCountries,
  getCountry,
  getStates,
  getState,
  getCities,
  getCity,
  getCountryById,
  getStateById,
  getCityById
};

export default locationService; 